import { useState } from "react";

export interface WatchTickerBadgeProps {
  /** Ticker symbol the badge opens; used for the accessible name. */
  ticker: string;
  /** Saved company logo as a data URI. When absent or failing to load, the badge shows the ticker's first two letters. */
  logoDataUri?: string | null;
  /** Font family for the letter fallback, taken from the design token. */
  fontFamily: string;
  /** Called when the badge is clicked. The click does not reach the parent row. */
  onOpen: () => void;
}

/**
 * Watch row badge showing the saved company logo, or the ticker letters when no logo is available.
 *
 * @example
 * <WatchTickerBadge ticker="AAPL" logoDataUri={watch.logo_data_uri} fontFamily={T.display} onOpen={open} />
 */
export default function WatchTickerBadge({ ticker, logoDataUri, fontFamily, onOpen }: WatchTickerBadgeProps) {
  const [failedLogo, setFailedLogo] = useState<string | null>(null);
  const showLogo = Boolean(logoDataUri) && logoDataUri !== failedLogo;

  return (
    <button
      type="button"
      style={{ fontFamily }}
      aria-label={`Open ${ticker}`}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
      className={showLogo ? "watches-ticker-badge watches-ticker-badge--logo" : "watches-ticker-badge"}
    >
      {showLogo ? (
        <img
          src={logoDataUri ?? undefined}
          alt=""
          className="watches-ticker-logo"
          onError={() => setFailedLogo(logoDataUri ?? null)}
        />
      ) : (
        ticker.slice(0, 2)
      )}
    </button>
  );
}
